import { css } from '@porsche-code/kado-styled-system/css';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  type IconColor,
  PButtonPure,
  PIcon,
} from '@porsche-design-system/components-react';
import { motion } from 'framer-motion';
import debounce from 'lodash/debounce';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const Wrapper = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'default',
  },
});

const Header = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 0,
  },
});

const actionsStyles = css({
  overflow: 'hidden',
  display: 'grid',
  gridAutoColumns: 'auto',
  gridAutoFlow: 'column',
  gridGap: 'sm',
  alignItems: 'center',
  marginRight: 'xs',
  padding: 'xs',
});

const Icon = styled(
  motion.create(PIcon),
  {
    base: {
      cursor: 'pointer',
    },

    variants: {
      disabled: {
        true: {
          cursor: 'not-allowed',
        },
      },
    },
  },
  { forwardProps: ['color'] },
);

export const Fab = ({
  title,
  children,
  color,
  disabled,
}: {
  title: ReactNode;
  children: ReactNode;
  color?: IconColor;
  disabled?: boolean;
}) => (
  <Header>
    {title}
    {children && (
      <Button color={color} disabled={disabled}>
        {children}
      </Button>
    )}
  </Header>
);

export const FabAction = styled(
  PButtonPure,
  {},
  { forwardProps: ['color', 'icon'], defaultProps: { hideLabel: true } },
);

const Button = ({
  onToggle,
  stayOpen = false,
  children,
  color,
  disabled = false,
  ...props
}: {
  onToggle?: (open: boolean) => void;
  stayOpen?: boolean;
  children: ReactNode;
  color?: IconColor;
  disabled?: boolean;
}) => {
  const [isExpanded, setExpand] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);

  // avoid closing the actions if mouseOver and click occur after each other
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const setExpandDebounced = useCallback(
    debounce(setExpand, 500, {
      leading: true,
      trailing: false,
    }),
    [setExpand],
  );

  const autoCollapse = useRef(0);

  useEffect(() => {
    let mounted = true;
    if (!hasFocus && !stayOpen) {
      autoCollapse.current = window.setTimeout(
        () => mounted && setExpand(false),
        1000,
      );
    } else if (autoCollapse.current) {
      window.clearTimeout(autoCollapse.current);
    }
    return () => {
      mounted = false;
    };
  }, [hasFocus, stayOpen]);

  const toggleRef = useRef(onToggle);
  toggleRef.current = onToggle;
  useEffect(() => {
    toggleRef.current?.(isExpanded);
  }, [isExpanded]);

  return (
    <Wrapper
      aria-disabled={disabled ? 'true' : 'false'}
      onPointerDown={(evt: React.PointerEvent<HTMLDivElement>) =>
        evt.stopPropagation()
      }
      onPointerEnter={() => {
        setHasFocus(true);
      }}
      onPointerLeave={() => {
        setHasFocus(false);
      }}
      {...props}
    >
      <motion.div
        className={actionsStyles}
        initial={{ width: 0 }}
        animate={isExpanded ? 'expanded' : 'collapsed'}
        variants={{
          expanded: {
            width: 'auto',
            opacity: 1,
          },
          collapsed: {
            width: 0,
            opacity: 0,
          },
        }}
        transition={{ ease: 'backInOut' }}
      >
        {children}
      </motion.div>
      <Icon
        data-testid="fab-toggle"
        name="menu-dots-vertical"
        color={color}
        animate={{ rotate: isExpanded ? 90 : 0 }}
        disabled={disabled}
        {...(!disabled && {
          onClick: () => setExpandDebounced((e) => !e),
          onPointerOver: () => setExpandDebounced(true),
        })}
      />
    </Wrapper>
  );
};
